// ----------------------------------------------------------------------------
// Song library: search, tag filters, table or cards, add (YouTube-link song),
// CSV/JSON import, delete, and the play-count sync. Song Studio uploads are a
// separate list (/admin/chord-library) and never appear here.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, LockKeyhole, Search, Trash2 } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { useSubscriptionFeatures } from '@admin/shared/hooks/useSubscriptionFeatures';
import { UpgradeFeatureModal } from '@app/components/UpgradeFeatureModal';
import { logger } from '@app/utils/logger';
import { songsApi } from '@app/api/organization';
import { Toast } from '@app/components/Notifications';
import { useMediaQuery, BP } from '@app/hooks/useMediaQuery';
import { Button, Chip, ConfirmDialog, EmptyState, ErrorState, IconButton, Input, KeyBadge, PageHeader, Segmented } from '@app/components/ui';
import type { Song } from '@app/types';
import { SongFormModal } from '../components/songs/SongFormModal';
import { ImportSongsModal, type ImportSongData } from '../components/songs/ImportSongsModal';
import { SongItem } from '../components/songs/SongItem';
import { EMPTY_SONG_FORM, lastPlayedLabel, type SongFormData } from '../components/songs/songUtils';

const COLS = 'minmax(220px,2.2fr) minmax(140px,1.4fr) 60px 60px 110px 70px 84px';

export const Songs: React.FC = () => {
  const { user } = useAuth();
  const { selectedGroupId } = useGroup();
  const { features } = useSubscriptionFeatures();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const wide = useMediaQuery(BP.wideNav);

  const [songs, setSongs] = useState<Song[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tag, setTag] = useState('All');
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [formData, setFormData] = useState<SongFormData>(EMPTY_SONG_FORM);
  const [formErrors, setFormErrors] = useState<Partial<Record<'title' | 'artist', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [showImportUpgrade, setShowImportUpgrade] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Song | null>(null);
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false);
  const [syncingUsage, setSyncingUsage] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const churchId = user?.churchId;
  const flash = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastType(type);
    setToastMessage(msg);
  };

  // Create → Add song (and the dashboard tile) arrive with ?add=1.
  useEffect(() => {
    if (params.get('add') === '1') {
      setIsAddOpen(true);
      params.delete('add');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    if (!churchId) {
      setLoading(false);
      return;
    }
    let live = true;
    (async () => {
      try {
        setLoading(true);
        setFailed(false);
        const data = await songsApi.getLibrary(churchId, undefined, undefined, 100, undefined, selectedGroupId ?? undefined);
        if (live) setSongs(data);
      } catch (error) {
        logger.error('Failed to fetch songs:', error);
        if (live) {
          setSongs([]);
          setFailed(true);
        }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [churchId, selectedGroupId, reloadKey]);

  // Recompute play counts from past services (backfill); also adds sung songs missing from the library
  const handleSyncUsage = async () => {
    if (!churchId || syncingUsage) return;
    setSyncingUsage(true);
    try {
      const result = await songsApi.recomputeUsage(churchId);
      const parts = [`${result.songsUpdated} song${result.songsUpdated === 1 ? '' : 's'} updated`];
      if (result.songsCreated > 0) parts.push(`${result.songsCreated} added to library`);
      if (result.songsSkippedByLimit > 0) parts.push(`${result.songsSkippedByLimit} not added (song limit reached)`);
      flash(`Play counts synced from ${result.plansScanned} past service${result.plansScanned === 1 ? '' : 's'}: ${parts.join(', ')}`);
      setReloadKey((k) => k + 1);
    } catch (error: any) {
      logger.error('Failed to sync play counts:', error);
      flash(error?.response?.data?.error || 'Failed to sync play counts', 'error');
    } finally {
      setSyncingUsage(false);
    }
  };

  const handleAddSong = async () => {
    if (!churchId || !user) return;
    const er: typeof formErrors = {};
    if (!formData.title.trim()) er.title = 'Add a title.';
    if (!formData.artist.trim()) er.artist = 'Add the artist.';
    setFormErrors(er);
    if (Object.keys(er).length) return;
    try {
      setSubmitting(true);
      const newSong = await songsApi.create(churchId, {
        churchId,
        title: formData.title.trim(),
        author: formData.artist.trim(),
        defaultKey: formData.key,
        defaultTempo: parseInt(formData.tempo) || undefined,
        ccliNumber: formData.ccliNumber || undefined,
        youtubeUrl: formData.youtubeUrl || undefined,
        createdBy: user.id,
      });
      setSongs((prev) => [newSong, ...prev]);
      setIsAddOpen(false);
      setFormData(EMPTY_SONG_FORM);
      flash(`"${newSong.title}" added to your library`);
    } catch (error: any) {
      logger.error('Failed to add song:', error);
      flash(error.message || 'Failed to add song', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSong = async (song: Song) => {
    if (!churchId) return;
    try {
      await songsApi.delete(churchId, song.id);
      setSongs((prev) => prev.filter((s) => s.id !== song.id));
      flash('Song deleted');
    } catch (error: any) {
      logger.error('Failed to delete song:', error);
      flash(error.message || 'Failed to delete song', 'error');
    }
  };

  const handleImportSongs = async (importData: ImportSongData[]) => {
    if (!churchId || !user) return;
    try {
      setSubmitting(true);
      setIsImportOpen(false);
      flash('Importing songs... This may take a moment.', 'info');
      // Use batch import API with duplicate detection
      const result = await songsApi.batchCreate(
        churchId,
        importData.map((songData) => ({
          churchId,
          title: songData.title,
          author: songData.author,
          defaultKey: songData.key,
          defaultTempo: songData.bpm || songData.tempo,
          ccliNumber: songData.ccliNumber,
          youtubeUrl: songData.youtubeUrl,
          createdBy: user.id,
        })),
      );
      if (result.created.length > 0) setSongs((prev) => [...prev, ...result.created]);
      const parts: string[] = [];
      if (result.created.length > 0) parts.push(`${result.created.length} imported`);
      if (result.duplicates.length > 0) parts.push(`${result.duplicates.length} skipped (already exist)`);
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
      flash(parts.length > 0 ? `Import complete: ${parts.join(', ')}` : 'No songs were imported', result.created.length > 0 ? 'success' : 'info');
    } catch (error: any) {
      logger.error('Import failed:', error);
      flash(error.message || 'Import failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const tags = useMemo(() => {
    const set = new Set<string>();
    songs.forEach((s) => s.tags?.forEach((t) => t && set.add(t)));
    return ['All', ...Array.from(set).sort()];
  }, [songs]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return songs.filter(
      (s) =>
        (tag === 'All' || s.tags?.includes(tag)) &&
        (!q || s.title.toLowerCase().includes(q) || s.author?.toLowerCase().includes(q)),
    );
  }, [songs, searchTerm, tag]);

  const open = useCallback((s: Song) => navigate(`/admin/songs/${s.id}`), [navigate]);
  const showTable = wide && view === 'table';

  return (
    <div>
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} type={toastType} />}

      <PageHeader
        eyebrow={loading ? 'Loading songs…' : `${songs.length} song${songs.length === 1 ? '' : 's'} in your library`}
        title="Song library"
        actions={
          <>
            <Button variant="ghost" onClick={() => setIsSyncConfirmOpen(true)} disabled={syncingUsage} data-testid="sync-play-counts-button">
              {syncingUsage ? 'Syncing…' : 'Sync play counts'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => (features.batchImport ? setIsImportOpen(true) : setShowImportUpgrade(true))}
              icon={!features.batchImport ? <LockKeyhole size={14} aria-hidden="true" /> : undefined}
              data-testid="import-songs-button"
            >
              Import CSV
              {/* Locked: say which plan unlocks it, so the admin learns what it costs. */}
              {!features.batchImport && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-amb-bg text-amb">Basic</span>}
            </Button>
            <Button variant="dark" onClick={() => setIsAddOpen(true)}>
              + Add song
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2.5 mb-[18px]">
        <div className="flex-1 min-w-[220px] max-w-[360px]">
          <Input
            height={40}
            pill
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search title or artist"
            aria-label="Search songs"
            leading={<Search size={16} strokeWidth={2} />}
            className="!bg-card !text-[14px]"
          />
        </div>
        {tags.length > 1 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by tag">
            {tags.map((t) => (
              <Chip key={t} on={tag === t} onClick={() => setTag(t)}>
                {t}
              </Chip>
            ))}
          </div>
        )}
        {wide && (
          <Segmented
            className="ml-auto"
            label="Library view"
            value={view}
            onChange={setView}
            options={[
              { value: 'table', label: 'Table' },
              { value: 'cards', label: 'Cards' },
            ]}
          />
        )}
      </div>

      {failed ? (
        <ErrorState title="Couldn't load your songs" body="Check your connection and try again." onRetry={() => setReloadKey((k) => k + 1)} />
      ) : loading ? (
        <div className="rounded-[22px] border border-line bg-card overflow-hidden" aria-busy="true">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5 border-b border-line last:border-0">
              <span className="wp-skeleton h-4 w-1/3 rounded-[6px]" />
              <span className="wp-skeleton h-4 w-1/5 rounded-[6px]" />
              <span className="wp-skeleton h-7 w-8 rounded-[8px] ml-auto" />
            </div>
          ))}
        </div>
      ) : songs.length === 0 ? (
        <EmptyState
          title="Your song library is empty"
          body="Add songs with a YouTube link, or import a list you already have."
          action={
            <Button onClick={() => setIsAddOpen(true)}>
              + Add song
            </Button>
          }
        />
      ) : filtered.length === 0 ? (
        <div className="py-[60px] text-center text-t4 text-[14px]">No songs match that search.</div>
      ) : showTable ? (
        <div className="rounded-[22px] border border-line bg-card overflow-auto">
          <div role="table" aria-label="Song library" className="min-w-[760px]">
            <div role="row" className="grid gap-3 px-5 py-3.5 border-b border-line wp-eyebrow" style={{ gridTemplateColumns: COLS }}>
              <span role="columnheader">Title</span>
              <span role="columnheader">Artist</span>
              <span role="columnheader">Key</span>
              <span role="columnheader">BPM</span>
              <span role="columnheader">Last played</span>
              <span role="columnheader">Plays</span>
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            {filtered.map((s) => (
              <div key={s.id} role="row" className="grid gap-3 items-center px-5 py-3 border-b border-line last:border-0 text-[14px] hover:bg-fill" style={{ gridTemplateColumns: COLS }}>
                <span role="cell" className="min-w-0">
                  <button type="button" onClick={() => open(s)} className="block min-w-0 max-w-full text-left text-t1 group">
                    <span className="block font-semibold truncate group-hover:text-acc">{s.title}</span>
                    <span className="block text-[12px] text-t4 mt-0.5 truncate">
                      {[s.tags?.[0], s.youtubeUrl ? 'YouTube' : null, s.lyrics ? 'Lyrics' : 'No lyrics'].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </span>
                <span role="cell" className="text-t3 truncate">
                  {s.author}
                </span>
                <span role="cell">
                  <KeyBadge k={s.defaultKey} size="sm" />
                </span>
                <span role="cell" className="font-mono text-t3 text-[13px]">
                  {s.defaultTempo ?? '–'}
                </span>
                <span role="cell" className="text-t3 text-[13px]">
                  {lastPlayedLabel(s.lastPlayed)}
                </span>
                <span role="cell" className="font-mono text-t3 text-[13px]">
                  {s.timesSung ?? 0}
                </span>
                <span role="cell" className="flex gap-1 justify-end">
                  <IconButton label={`Open ${s.title}`} tone="outline" size={32} onClick={() => open(s)}>
                    <Eye size={14} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                  <IconButton
                    label={`Delete ${s.title}`}
                    tone="outline"
                    size={32}
                    className="hover:!text-acc hover:!border-[rgba(208,48,69,.4)]"
                    onClick={() => setDeleteTarget(s)}
                  >
                    <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                  </IconButton>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))' }}>
          {filtered.map((s) => (
            <SongItem key={s.id} song={s} onOpenDetail={open} onDelete={() => setDeleteTarget(s)} />
          ))}
        </div>
      )}

      <SongFormModal
        isOpen={isAddOpen}
        mode="add"
        onClose={() => {
          setIsAddOpen(false);
          setFormData(EMPTY_SONG_FORM);
          setFormErrors({});
        }}
        submitting={submitting}
        formData={formData}
        errors={formErrors}
        onChange={(patch) => setFormData({ ...formData, ...patch })}
        onSubmit={handleAddSong}
      />

      <ImportSongsModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} onImport={handleImportSongs} />

      <UpgradeFeatureModal
        isOpen={showImportUpgrade}
        onClose={() => setShowImportUpgrade(false)}
        featureName="Batch Song Import"
        description="Bring your existing song catalog into ChurchNow in one upload, with duplicate detection built in."
        requiredPlan="Basic"
      />

      <ConfirmDialog
        isOpen={isSyncConfirmOpen}
        onClose={() => setIsSyncConfirmOpen(false)}
        onConfirm={handleSyncUsage}
        title="Sync play counts"
        message="Scan all past services and update how many times each song has been sung. Songs that were sung but aren't in the library yet will be added. This is safe to run more than once."
        confirmText="Sync"
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => (deleteTarget ? handleDeleteSong(deleteTarget) : undefined)}
        type="danger"
        title={deleteTarget ? `Delete "${deleteTarget.title}"?` : 'Delete song?'}
        message="It will be removed from the library and can no longer be added to setlists. Past setlists keep their record of it."
        confirmText="Delete song"
      />
    </div>
  );
};
